import { CodeGenerationContextProvider } from '@common/git/CodeGeneration/context';
import { ProjectContextProvider } from '@common/git/Project/context';
import { useSCIMUsersAndTeamsList } from '@common/SCIM/hooks';
import { Entity } from '@common/types/Entity';
import { useIsVerticalApp } from '@common/VerticalApps/utils';
import { SchemaType } from '@pages/Metadata/config/types';
import { theme } from '@prophecy/theme';
import { Badges, Centered, Spinner, Stack } from '@prophecy/ui-v3';
import { DefaultRecordType } from '@prophecy/ui-v3/Table/types';
import { FROZEN_EMPTY_OBJECT } from '@prophecy/utils/constants';
import { useEditionConfig } from 'frontend/core/src/context/edition';
import { useMemo } from 'react';

import { useGraphQlQuery } from '../../../../data/util';
import { useTeamInfoSchema } from '../../config/team';
import { getFieldValue, getQueryFromTableSchema } from '../../util';
import { EditEntity } from '../EditEntity';
import { InfoEntityFooter } from '../EntityInfo/Footer';
import { useHandleEntityUpdate } from '../EntityInfo/utils';

export const TeamInfoEntity = ({ teamId }: { teamId: string }) => {
  const schema = useTeamInfoSchema();
  const entity = schema.ownerEntity as Entity;
  const globalTeamName = useEditionConfig().globalTeamName;
  const isVerticalApp = useIsVerticalApp();

  const variables = { uid: teamId };
  const EntityDetailQuery = getQueryFromTableSchema(schema);
  const { data, isError, isLoading } = useGraphQlQuery<DefaultRecordType>(
    [entity, entity + 'Info', variables],
    EntityDetailQuery,
    undefined,
    variables
  );
  const { scimTeamsData, isTeamsLoading } = useSCIMUsersAndTeamsList(true, false);
  const currentData = data?.[entity] || (FROZEN_EMPTY_OBJECT as DefaultRecordType);
  const isPersonal = currentData.teamKind === 'ProphecyManagedPersonal';

  const { loadingMap, fieldKey, handleSubmit, onError, onSuccess } = useHandleEntityUpdate({
    entity,
    currentData,
    schema: schema as unknown as SchemaType<DefaultRecordType>,
    uid: teamId
  });

  const updatedSchema = useMemo(() => {
    return {
      ...(schema || {}),
      fields: schema?.fields?.filter((field) => {
        if (field.key === 'entraIdEnabled' && isVerticalApp) {
          return false;
        }
        if (field.key === 'assumedRoleEnabled' && isVerticalApp) {
          return false;
        }
        if (field.key === 'transpilerEnabled' && isVerticalApp) {
          return false;
        }
        if (field.key === 'teamKind') {
          return false;
        }
        return field;
      })
    };
  }, [schema, isVerticalApp]);

  if (isError || isLoading) {
    return (
      <Centered>{isLoading ? <Spinner spinning={true} /> : <Badges tone='error'>Some error occurred</Badges>}</Centered>
    );
  }

  const fields = updatedSchema?.fields || [];
  const editableFields = fields.filter((f) => f.editable && f.key !== 'button' && !f.hidden);
  const isSCIMTeam = scimTeamsData.some((team) => team.prophecyUID === teamId);
  return (
    <ProjectContextProvider projectId={''}>
      <CodeGenerationContextProvider>
        <Stack gap={theme.spaces.x24} height='100%' align='space-between'>
          <Stack gap={theme.spaces.x12} key={fieldKey}>
            {editableFields.map((field) => {
              const rowData = getFieldValue(currentData, field);
              const currentKey = field.key as string;
              const Component = field.Component || EditEntity;
              const fieldValue = rowData[currentKey as keyof typeof rowData];
              const isGlobalName = currentKey === 'name' && fieldValue === globalTeamName;
              const title = field.title;
              return (
                <Stack gap={theme.spaces.x4} key={currentKey}>
                  <Component
                    title={title}
                    entity={entity}
                    key={currentKey}
                    uid={teamId}
                    disabled={isGlobalName || field.disabled}
                    tooltip={isGlobalName ? 'Global team name cannot be edited' : field.tooltip}
                    titleKey={currentKey}
                    placeholder={currentKey}
                    required={field.required}
                    isLoading={loadingMap[currentKey]}
                    handleSubmit={(value?: string | boolean) =>
                      value !== fieldValue ? handleSubmit(value as string, field) : null
                    }
                    value={fieldValue}
                    saveOnChange
                    onSuccess={onSuccess}
                    onError={onError}
                    data-test-id={`info-entity-${currentKey}-${entity}-input`}
                  />
                </Stack>
              );
            })}
          </Stack>
          <InfoEntityFooter
            data={currentData}
            schema={updatedSchema}
            variables={variables}
            disableDeleteMessage={
              isTeamsLoading
                ? 'Loading...'
                : isSCIMTeam
                  ? 'You are not authorized to delete this team.'
                  : isPersonal
                    ? 'Deleting personal team is not allowed'
                    : undefined
            }
          />
        </Stack>
      </CodeGenerationContextProvider>
    </ProjectContextProvider>
  );
};
